import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { AgentAnalyzingPanel } from "./AgentAnalyzingPanel";
import { FrozenAgentPanel } from "./FrozenAgentPanel";
import { ArtifactPreviewCard } from "./ArtifactPreviewCard";
import { styles } from "./styles";
import { markdownStyles } from "./styles";

export function MessageList({
  conversationHistory,
  isAnalyzing,
  progress,
  analysisComplete,
  progressExpanded,
  setProgressExpanded,
  progressPanelRef,
  avatarSrc,
  // artifacts mapped by runId
  artifactsByRunId = {},
  onOpenArtifact,
  activeAgents = [],
  isDockOpen = false,
}) {
  const [collapsedFrozenIds, setCollapsedFrozenIds] = useState(new Set());
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversationHistory]);

  const isFrozenExpanded = (id) => !collapsedFrozenIds.has(id);
  const toggleFrozenExpanded = (id) =>
    setCollapsedFrozenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // console.log("avatarSrc", avatarSrc);
  return (
    <div style={styles.messagesArea}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {conversationHistory.map((msg) => {
          const isPendingAssistant = msg.type === "assistant" && msg.pending;

          let messagesArray = [];
          if (msg.type === "assistant" && !isPendingAssistant) {
            if (Array.isArray(msg.message)) {
              messagesArray = msg.message;
            } else if (typeof msg.message === "string") {
              const s = msg.message.trim();
              if (s.length) {
                try {
                  const parsed = JSON.parse(s);
                  messagesArray = Array.isArray(parsed) ? parsed : [s];
                } catch {
                  messagesArray = [s];
                }
              }
            }
          }
          messagesArray = messagesArray
            .filter((line) => typeof line === "string")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

          const artifactForThisMsg =
            msg.type === "assistant" ? artifactsByRunId?.[msg.runId] : null;
          const hasArtifacts =
            artifactForThisMsg &&
            (artifactForThisMsg.artifact_image_url ||
              artifactForThisMsg.report_url ||
              (artifactForThisMsg.artifact_plan &&
                artifactForThisMsg.artifact_plan.artifact_possible === true));

          return (
            <div key={msg.ts} style={styles.messageContainer}>
              {msg.type === "user" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  <div style={styles.userMessage}>{msg.message}</div>
                  <img
                    src={msg.avatar || avatarSrc}
                    alt="User Avatar"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "2px solid #333",
                      flexShrink: 0,
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={
                    msg.type === "assistant"
                      ? styles.assistantMessage
                      : styles.systemMessage
                  }
                >
                  {msg.type === "assistant" ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {isPendingAssistant ? (
                        (isAnalyzing ||
                          progress.length > 0 ||
                          analysisComplete) && (
                          <AgentAnalyzingPanel
                            activeAgents={activeAgents}
                            isAnalyzing={isAnalyzing}
                            progress={progress}
                            analysisComplete={analysisComplete}
                            progressExpanded={progressExpanded}
                            onToggleExpand={() =>
                              setProgressExpanded(!progressExpanded)
                            }
                            progressPanelRef={progressPanelRef}
                            isDockOpen={isDockOpen}
                          />
                        )
                      ) : Array.isArray(msg.logsSnapshot) &&
                        msg.logsSnapshot.length > 0 ? (
                        <FrozenAgentPanel
                          logs={msg.logsSnapshot}
                          expanded={isFrozenExpanded(
                            msg.runId ?? String(msg.ts)
                          )}
                          onToggle={() =>
                            toggleFrozenExpanded(msg.runId ?? String(msg.ts))
                          }
                        />
                      ) : null}

                      <div style={markdownStyles.container}>
                        <ReactMarkdown
                          components={{
                            h1: ({ node, ...props }) => (
                              <h1 style={markdownStyles.h1} {...props} />
                            ),
                            h2: ({ node, ...props }) => (
                              <h2 style={markdownStyles.h2} {...props} />
                            ),
                            p: ({ node, ...props }) => (
                              <p style={markdownStyles.p} {...props} />
                            ),
                            code: ({ node, inline, ...props }) => (
                              <code style={markdownStyles.code} {...props} />
                            ),
                            pre: ({ node, ...props }) => (
                              <pre style={markdownStyles.pre} {...props} />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul style={markdownStyles.ul} {...props} />
                            ),
                            li: ({ node, ...props }) => (
                              <li style={markdownStyles.li} {...props} />
                            ),
                            strong: ({ node, ...props }) => (
                              <strong
                                style={markdownStyles.strong}
                                {...props}
                              />
                            ),
                            a: ({ href, children, ...props }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={markdownStyles.a}
                                onClick={(e) => {
                                  // Always open as a new external link
                                  e.preventDefault();
                                  window.open(
                                    href,
                                    "_blank",
                                    "noopener,noreferrer"
                                  );
                                }}
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {messagesArray.join("\n")}
                        </ReactMarkdown>
                      </div>

                      {/* Persist artifact preview per assistant response */}
                      {!isPendingAssistant && hasArtifacts ? (
                        <div style={{ marginTop: "4px" }}>
                          <ArtifactPreviewCard
                            qa={artifactForThisMsg}
                            onOpen={() => onOpenArtifact?.(msg.runId)}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    msg.message
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
